import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { chatbotAdminApi } from '../../../services/api'
import Button from '../../../components/ui/Button'
import Card from '../../../components/ui/Card'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import AdminFormField, { adminInputClass } from '../../../components/admin/AdminFormField'

const EMPTY = {
  category: 'General',
  topic_en: '',
  topic_ar: '',
  content_en: '',
  content_ar: '',
  keywords: '',
  related_url: '',
  priority: 50,
  is_active: true,
  is_suggested: false,
}

export default function AdminAiKnowledge() {
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [categories, setCategories] = useState([])
  const [routes, setRoutes] = useState([])
  const [form, setForm] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = () => {
    setLoading(true)
    chatbotAdminApi
      .getKnowledge()
      .then(({ data }) => {
        setItems(data.data || [])
        setCategories(data.categories || [])
        setRoutes(data.allowed_routes || [])
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const handleSave = async (event) => {
    event.preventDefault()
    setSaving(true)
    setError('')
    try {
      const payload = {
        ...form,
        priority: Number(form.priority) || 50,
        related_url: form.related_url || null,
      }
      if (form.id) await chatbotAdminApi.updateKnowledge(form.id, payload)
      else await chatbotAdminApi.createKnowledge(payload)
      setForm(null)
      load()
    } catch (err) {
      setError(err?.response?.data?.message || t('admin.aiAssistant.saveError'))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.aiAssistant.confirmDelete'))) return
    await chatbotAdminApi.deleteKnowledge(id)
    if (form?.id === id) setForm(null)
    load()
  }

  if (loading) return <LoadingSpinner className="py-20" />

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <Button variant="primary" size="sm" onClick={() => setForm({ ...EMPTY })}>
          {t('admin.aiAssistant.add')}
        </Button>
      </div>

      {form ? (
        <Card className="mb-6">
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <AdminFormField label={t('admin.aiAssistant.category')}>
                <select
                  className={adminInputClass}
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  required
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </AdminFormField>
              <AdminFormField label={t('admin.aiAssistant.priority')}>
                <input
                  type="number"
                  className={adminInputClass}
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                />
              </AdminFormField>
              <AdminFormField label={t('admin.aiAssistant.topicEn')}>
                <input
                  className={adminInputClass}
                  value={form.topic_en}
                  onChange={(e) => setForm({ ...form, topic_en: e.target.value })}
                  required
                />
              </AdminFormField>
              <AdminFormField label={t('admin.aiAssistant.topicAr')}>
                <input
                  className={adminInputClass}
                  value={form.topic_ar}
                  onChange={(e) => setForm({ ...form, topic_ar: e.target.value })}
                  required
                  dir="rtl"
                />
              </AdminFormField>
              <AdminFormField label={t('admin.aiAssistant.contentEn')} className="md:col-span-2">
                <textarea
                  className={adminInputClass}
                  rows={4}
                  value={form.content_en}
                  onChange={(e) => setForm({ ...form, content_en: e.target.value })}
                  required
                />
              </AdminFormField>
              <AdminFormField label={t('admin.aiAssistant.contentAr')} className="md:col-span-2">
                <textarea
                  className={adminInputClass}
                  rows={4}
                  value={form.content_ar}
                  onChange={(e) => setForm({ ...form, content_ar: e.target.value })}
                  required
                  dir="rtl"
                />
              </AdminFormField>
              <AdminFormField label={t('admin.aiAssistant.keywords')}>
                <input
                  className={adminInputClass}
                  value={form.keywords || ''}
                  onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                />
              </AdminFormField>
              <AdminFormField label={t('admin.aiAssistant.relatedUrl')}>
                <select
                  className={adminInputClass}
                  value={form.related_url || ''}
                  onChange={(e) => setForm({ ...form, related_url: e.target.value })}
                >
                  <option value="">{t('admin.aiAssistant.noLink')}</option>
                  {routes.map((route) => (
                    <option key={route} value={route}>
                      {route}
                    </option>
                  ))}
                </select>
              </AdminFormField>
            </div>
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_active)}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                {t('admin.aiAssistant.active')}
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={Boolean(form.is_suggested)}
                  onChange={(e) => setForm({ ...form, is_suggested: e.target.checked })}
                />
                {t('admin.aiAssistant.suggested')}
              </label>
            </div>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <div className="flex gap-2">
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? t('common.loading') : t('buttons.save')}
              </Button>
              <Button type="button" variant="outlineDark" onClick={() => setForm(null)}>
                {t('buttons.cancel')}
              </Button>
            </div>
          </form>
        </Card>
      ) : null}

      <Card padding={false} className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="border-b border-border bg-primary/5">
            <tr className="text-start">
              <th className="px-4 py-3">{t('admin.aiAssistant.category')}</th>
              <th className="px-4 py-3">{t('admin.aiAssistant.topicEn')}</th>
              <th className="px-4 py-3">{t('admin.aiAssistant.active')}</th>
              <th className="px-4 py-3">{t('admin.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id} className="border-b border-border/60">
                <td className="px-4 py-3">{item.category}</td>
                <td className="px-4 py-3">{item.topic_en}</td>
                <td className="px-4 py-3">{item.is_active ? 'Yes' : 'No'}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outlineDark" onClick={() => setForm({ ...item })}>
                      {t('buttons.edit')}
                    </Button>
                    <Button size="sm" variant="outlineDark" onClick={() => handleDelete(item.id)}>
                      {t('buttons.delete')}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
